import type { DeviceListItemWire, LocationWire } from '../api/client'
import type { NavLinkItem } from './navigation'

/**
 * The command palette's model: what a query finds, in the order an operator wants it.
 *
 * Pure, so the matching — every word must match somewhere, in any order — is tested without a page.
 * "phoenix reg 2" finds Register 2 at the Phoenix store, which a single substring search over one
 * field at a time cannot (Plans/experience-review-2026-10-10.md: the Support app's search returned
 * nothing for "best burger san diego register 2").
 */

export type PaletteResult =
  | {
      kind: 'device'
      id: string
      title: string
      detail: string
      online: boolean
      canConnect: boolean
    }
  | { kind: 'store'; id: string; title: string; detail: string }
  | { kind: 'page'; to: string; title: string; detail: string }
  /**
   * One setting, wherever it is set.
   *
   * The reviewer's complaint was that it is "challenging to figure out where the info I need is".
   * A setting is the hardest case: brightness lives inside Display & branding inside a profile
   * inside Settings Profiles, and nothing short of knowing that finds it. Typing "brightness" now
   * does (Plans/esper-ux-parity.md, Phase 7).
   */
  | { kind: 'setting'; to: string; title: string; detail: string }

/** The query as lower-case words. */
export function words(query: string): string[] {
  return query.toLowerCase().split(/\s+/).filter(Boolean)
}

/** Every word appears somewhere in the haystack. */
export function matchesAll(tokens: string[], ...fields: Array<string | null | undefined>): boolean {
  const haystack = fields.filter(Boolean).join(' ').toLowerCase()
  return tokens.every((token) => haystack.includes(token))
}

/**
 * The word to ask the relay with: the longest, which narrows its substring search the most. The rest
 * are applied here, across every field, so a word can match the device's name or its store's.
 */
export function serverSearchTerm(query: string): string {
  return words(query).reduce((longest, word) => (word.length > longest.length ? word : longest), '')
}

const MAX_DEVICES = 8
const MAX_STORES = 5
const MAX_PAGES = 5
const MAX_SETTINGS = 6

export function paletteResults(
  query: string,
  {
    devices = [],
    stores = [],
    pages = [],
    settings = [],
  }: {
    devices?: DeviceListItemWire[]
    stores?: LocationWire[]
    pages?: NavLinkItem[]
    settings?: PaletteSetting[]
  },
): PaletteResult[] {
  const tokens = words(query)
  if (tokens.length === 0) return []

  const deviceResults: PaletteResult[] = devices
    .filter((d) =>
      matchesAll(
        tokens,
        d.name,
        d.device_id,
        d.location_name,
        d.store_number,
        d.brand_name,
        d.device_type_name,
      ),
    )
    // Online devices first: they are the ones that can be reached right now.
    .sort((a, b) => Number(b.is_online) - Number(a.is_online))
    .slice(0, MAX_DEVICES)
    .map((d) => ({
      kind: 'device',
      id: d.id,
      title: d.name ?? d.device_id,
      detail: [d.location_name, d.device_type_name, d.is_online ? 'Online' : 'Offline']
        .filter(Boolean)
        .join(' · '),
      online: d.is_online,
      canConnect: d.is_online && (d.services?.includes('video') ?? false),
    }))

  const storeResults: PaletteResult[] = stores
    .filter((s) => matchesAll(tokens, s.name, s.store_number, s.city, s.state))
    .slice(0, MAX_STORES)
    .map((s) => ({
      kind: 'store',
      id: s.id,
      title: s.name,
      detail: [
        s.store_number ? `Site ${s.store_number}` : null,
        [s.city, s.state].filter(Boolean).join(', ') || null,
      ]
        .filter(Boolean)
        .join(' · '),
    }))

  const pageResults: PaletteResult[] = pages
    .filter((p) => matchesAll(tokens, p.label, p.description))
    .slice(0, MAX_PAGES)
    .map((p) => ({ kind: 'page', to: p.to, title: p.label, detail: p.description ?? 'Go to page' }))

  const settingResults: PaletteResult[] = settings
    .filter((setting) => matchesAll(tokens, setting.label, setting.kindLabel, setting.hint, setting.key))
    .slice(0, MAX_SETTINGS)
    .map((setting) => ({
      kind: 'setting',
      // The Setting Explorer answers the question somebody is really asking — where is this set,
      // and what is it across the fleet — rather than dropping them in one profile's form.
      to: `/settings/explorer?kind=${encodeURIComponent(setting.kind)}&key=${encodeURIComponent(setting.key)}`,
      title: setting.label,
      detail: `Setting · ${setting.kindLabel}`,
    }))

  return [...deviceResults, ...storeResults, ...settingResults, ...pageResults]
}

/** One setting the palette can find, flattened from the relay's settings schema. */
export interface PaletteSetting {
  kind: string
  kindLabel: string
  key: string
  label: string
  hint?: string | null
}

/** Every setting in the schema, as the palette wants them. */
export function settingsForPalette(
  kinds: Array<{ kind: string; label: string; fields: Array<{ key: string; label: string; hint?: string | null }> }>,
): PaletteSetting[] {
  return kinds.flatMap((kind) =>
    kind.fields.map((field) => ({
      kind: kind.kind,
      kindLabel: kind.label,
      key: field.key,
      label: field.label,
      hint: field.hint,
    })),
  )
}
